/**
 * ============================================================================
 * SupplierRiskCard — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: single card in the bucketed Risk Ranking Dashboard. Click opens
 *          the drawer with the supplier's risk-history sparkline.
 *
 * VISUAL: white surface, 14 px radius, hover-lift, prominent risk number,
 *  criticality dot anchors the supplier's QA-side classification (separate
 *  from the risk bucket the card lives in).
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RiskRankingDashboard.module.css';
import type { RiskRankingCard } from './useRiskRankingQuery';

const CRIT_DOT: Record<RiskRankingCard['criticality'], string> = {
  CRITICAL: styles.dotCritical,
  MAJOR: styles.dotWarn,
  MINOR: styles.dotInfo,
  LOW: styles.dotNeutral,
};

export interface SupplierRiskCardProps {
  card: RiskRankingCard;
  onOpen: (card: RiskRankingCard) => void;
}

export function SupplierRiskCard({ card, onOpen }: SupplierRiskCardProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  const critLabel = t(`supplier.criticality.${card.criticality}`, {
    defaultValue: card.criticality,
  });

  return (
    <button
      type="button"
      className={styles.card}
      onClick={() => onOpen(card)}
      aria-label={t('supplier.riskRanking.cardAria', {
        defaultValue:
          'Open risk drawer for {{name}}, score {{score}}, criticality {{crit}}',
        name: card.name,
        score: nf.format(card.risk_score),
        crit: critLabel,
      })}
    >
      <header className={styles.cardHeader}>
        <span className={styles.cardName}>{card.name}</span>
        <span
          className={`${styles.dot} ${CRIT_DOT[card.criticality]}`}
          aria-hidden="true"
        />
      </header>
      <div className={styles.cardScoreRow}>
        <span className={styles.cardScore}>{nf.format(card.risk_score)}</span>
        <span className={styles.cardScoreLabel}>
          {t('supplier.riskRanking.cardScoreLabel', {
            defaultValue: 'Risk score',
          })}
        </span>
      </div>
      <footer className={styles.cardFooter}>
        <span className={styles.cardCritText}>{critLabel}</span>
        {card.last_assessment_at_utc ? (
          <time dateTime={card.last_assessment_at_utc} className={styles.cardTs}>
            {dateFmt.format(new Date(card.last_assessment_at_utc))}
          </time>
        ) : (
          <span className={styles.cardTs}>
            {t('supplier.riskRanking.noAssessment', {
              defaultValue: 'No assessment yet',
            })}
          </span>
        )}
      </footer>
    </button>
  );
}
